import type {
  AiPredictionRead,
  TimelyPresentableAgentInterval,
} from '@timelyapp/integration-cloud-api-spec';
import type { StandardInterval } from '@timelyapp/integration-cloud-sdk/utils';
import { difference } from 'lodash';
import type { LightProject, Project, ProjectBase } from 'types/project';

/**
 * Determines if a prediction can be saved as `hour`,
 * by ensuring project is usable, tags match, etc.
 */
export const isTicPredictionValidAsHour = (params: {
  prediction:
    | AiPredictionRead
    | StandardInterval<AiPredictionRead>
    | TimelyPresentableAgentInterval;
  /** NB: Light projects cannot guarantee hour validity, and may be incorrect */
  project: ProjectBase | LightProject | Project | null;
  /** When passed, and full `Project` is passed, will verify user exists in `project.users` */
  userId?: number | null;
}): boolean => {
  const { prediction, project, userId } = params;
  const { inferred } = prediction;

  let validAsHour = inferred.valid_as_hour; // Start with AI assumption..

  // Labels matching
  const hasFullProject = project && 'label_ids' in project;
  const inferredLabelIds = inferred.timely_label_ids || [];
  const requiredLabelIds = (hasFullProject && project.required_label_ids) || [];

  // No predicted labels, and no required labels on target project is OK
  if (!inferredLabelIds.length && !requiredLabelIds.length) {
    validAsHour = true;
  }
  // If project labels are required, make sure the prediction covers the requirement
  else if (requiredLabelIds.length) {
    const missingRequiredLabels = difference(requiredLabelIds, inferredLabelIds);
    validAsHour = missingRequiredLabels.length === 0;
  }

  if (hasFullProject && project.required_notes && !inferred.note) {
    validAsHour = false;
  }

  // If we have user assignments on projects,
  if (hasFullProject && project.users?.length) {
    const userIsOnProject = project.users.some(({ user_id }) => user_id === userId);
    if (!userIsOnProject) validAsHour = false;
  }

  // Check if the inferred project ID matches the given project ID
  if (inferred.timely_project_id !== project?.id) {
    validAsHour = false;
  }

  // Check if project exists and is active
  if (!project || !project.active) {
    validAsHour = false;
  }

  return validAsHour;
};
